import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import axios from "axios";
import type { ColumnsType } from "antd/es/table";
import API_CONFIG from "../../config/api";
import { useSelection } from "../../SelectionContext";
import { useAuth } from "../../context/AuthContext";

const { Text } = Typography;

type HotelProperty = {
  id: number;
  name: string;
};

type HotelAccount = {
  id: number;
  property_id: number;
  property_name?: string | null;
  account_type: "corporate" | "agency" | "group";
  name: string;
  contact_name?: string | null;
  email?: string | null;
  phone?: string | null;
  tax_number?: string | null;
  address?: string | null;
  city?: string | null;
  country?: string | null;
  payment_terms?: string | null;
  credit_limit?: number | null;
  is_active?: number | boolean | null;
};

type AccountFormValues = {
  propertyId: number;
  accountType: "corporate" | "agency" | "group";
  name: string;
  contactName?: string;
  email?: string;
  phone?: string;
  taxNumber?: string;
  address?: string;
  city?: string;
  country?: string;
  paymentTerms?: string;
  creditLimit?: number;
  isActive?: boolean;
};

const accountTypeLabels: Record<HotelAccount["account_type"], string> = {
  corporate: "Entreprise",
  agency: "Agence",
  group: "Groupe",
};

const AccountsTab: React.FC = () => {
  const { selectedCompany } = useSelection();
  const { user, hasPermission } = useAuth();
  const [properties, setProperties] = useState<HotelProperty[]>([]);
  const [accounts, setAccounts] = useState<HotelAccount[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [filters, setFilters] = useState({
    propertyId: "all" as number | "all",
    accountType: "all" as HotelAccount["account_type"] | "all",
    isActive: "all" as "all" | "true" | "false",
    search: "",
  });
  const [editingAccount, setEditingAccount] = useState<HotelAccount | null>(
    null
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm<AccountFormValues>();

  const requiresCompanySelection = Boolean(user?.is_superadmin && !selectedCompany);
  const canCreate = Boolean(hasPermission?.("Hotellerie.Comptes.create"));
  const canEdit = Boolean(hasPermission?.("Hotellerie.Comptes.edit"));
  const canDelete = Boolean(hasPermission?.("Hotellerie.Comptes.delete"));
  const hasProperties = properties.length > 0;

  const fetchProperties = useCallback(async () => {
    if (requiresCompanySelection) {
      return;
    }
    try {
      const params = selectedCompany ? { company_id: selectedCompany } : {};
      const response = await axios.get(`${API_CONFIG.API_URL}/hotel/properties`, {
        params,
      });
      if (response.data.success) {
        setProperties(response.data.data || []);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error fetching properties:", error);
      message.error(
        error.response?.data?.message || "Erreur lors du chargement des proprietes"
      );
    }
  }, [requiresCompanySelection, selectedCompany]);

  const fetchAccounts = useCallback(async () => {
    if (requiresCompanySelection) {
      return;
    }
    setLoadingAccounts(true);
    try {
      const params: Record<string, string | number> = {};
      if (selectedCompany) {
        params.company_id = selectedCompany;
      }
      if (filters.propertyId !== "all") {
        params.property_id = filters.propertyId;
      }
      if (filters.accountType !== "all") {
        params.account_type = filters.accountType;
      }
      if (filters.isActive !== "all") {
        params.is_active = filters.isActive;
      }
      const response = await axios.get(`${API_CONFIG.API_URL}/hotel/accounts`, {
        params,
      });
      if (response.data.success) {
        setAccounts(response.data.data || []);
      }
    } catch (error: any) {
      console.error("[HOTEL] Error fetching accounts:", error);
      message.error(
        error.response?.data?.message || "Erreur lors du chargement des comptes"
      );
    } finally {
      setLoadingAccounts(false);
    }
  }, [
    filters.accountType,
    filters.isActive,
    filters.propertyId,
    requiresCompanySelection,
    selectedCompany,
  ]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const filteredAccounts = useMemo(() => {
    const search = filters.search.trim().toLowerCase();
    if (!search) {
      return accounts;
    }
    return accounts.filter((account) =>
      [account.name, account.contact_name, account.email, account.phone]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(search))
    );
  }, [accounts, filters.search]);

  const openCreateModal = () => {
    setEditingAccount(null);
    form.resetFields();
    form.setFieldsValue({
      isActive: true,
    });
    setIsModalOpen(true);
  };

  const openEditModal = (account: HotelAccount) => {
    setEditingAccount(account);
    form.setFieldsValue({
      propertyId: account.property_id,
      accountType: account.account_type,
      name: account.name,
      contactName: account.contact_name || undefined,
      email: account.email || undefined,
      phone: account.phone || undefined,
      taxNumber: account.tax_number || undefined,
      address: account.address || undefined,
      city: account.city || undefined,
      country: account.country || undefined,
      paymentTerms: account.payment_terms || undefined,
      creditLimit: account.credit_limit ?? undefined,
      isActive: Boolean(account.is_active),
    });
    setIsModalOpen(true);
  };

  const handleSaveAccount = async () => {
    const values = await form.validateFields();
    if (!editingAccount && !canCreate) {
      message.warning("Vous n'avez pas les droits pour creer un compte.");
      return;
    }
    if (editingAccount && !canEdit) {
      message.warning("Vous n'avez pas les droits pour modifier ce compte.");
      return;
    }

    const payload = {
      ...(selectedCompany ? { company_id: selectedCompany } : {}),
      property_id: values.propertyId,
      account_type: values.accountType,
      name: values.name,
      contact_name: values.contactName || null,
      email: values.email || null,
      phone: values.phone || null,
      tax_number: values.taxNumber || null,
      address: values.address || null,
      city: values.city || null,
      country: values.country || null,
      payment_terms: values.paymentTerms || null,
      credit_limit: values.creditLimit ?? 0,
      is_active: values.isActive ? 1 : 0,
    };

    try {
      if (editingAccount) {
        const response = await axios.put(
          `${API_CONFIG.API_URL}/hotel/accounts/${editingAccount.id}`,
          payload
        );
        if (response.data.success) {
          message.success("Compte mis a jour.");
        }
      } else {
        const response = await axios.post(
          `${API_CONFIG.API_URL}/hotel/accounts`,
          payload
        );
        if (response.data.success) {
          message.success("Compte cree.");
        }
      }
      setIsModalOpen(false);
      setEditingAccount(null);
      fetchAccounts();
    } catch (error: any) {
      console.error("[HOTEL] Error saving account:", error);
      message.error(
        error.response?.data?.message ||
          "Erreur lors de la sauvegarde du compte"
      );
    }
  };

  const confirmDeleteAccount = (account: HotelAccount) => {
    if (!canDelete) {
      message.warning("Vous n'avez pas les droits pour supprimer ce compte.");
      return;
    }
    Modal.confirm({
      title: "Supprimer le compte",
      content: `Confirmer la suppression de ${account.name} ?`,
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          const params = selectedCompany ? { company_id: selectedCompany } : {};
          const response = await axios.delete(
            `${API_CONFIG.API_URL}/hotel/accounts/${account.id}`,
            { params }
          );
          if (response.data.success) {
            message.success("Compte supprime.");
            fetchAccounts();
          }
        } catch (error: any) {
          console.error("[HOTEL] Error deleting account:", error);
          message.error(
            error.response?.data?.message ||
              "Erreur lors de la suppression du compte"
          );
        }
      },
    });
  };

  const columns: ColumnsType<HotelAccount> = [
    {
      title: "Compte",
      key: "account",
      render: (_value, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{record.name}</Text>
          <Text type="secondary">{accountTypeLabels[record.account_type]}</Text>
        </Space>
      ),
    },
    {
      title: "Propriete",
      dataIndex: "property_name",
      key: "property_name",
      render: (value: string | null, record) =>
        value || `Propriete #${record.property_id}`,
    },
    {
      title: "Contact",
      key: "contact",
      render: (_value, record) =>
        record.contact_name || record.email || record.phone || "-",
    },
    {
      title: "Credit",
      dataIndex: "credit_limit",
      key: "credit_limit",
      render: (value: number | null) => (value ? value : "-"),
    },
    {
      title: "Statut",
      dataIndex: "is_active",
      key: "is_active",
      render: (value: number | boolean | null) => (
        <Tag color={value ? "green" : "default"}>
          {value ? "Actif" : "Inactif"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_value, record) => (
        <Space size={4}>
          {canEdit ? (
            <Button type="link" onClick={() => openEditModal(record)}>
              Modifier
            </Button>
          ) : null}
          {canDelete ? (
            <Button type="link" danger onClick={() => confirmDeleteAccount(record)}>
              Supprimer
            </Button>
          ) : null}
        </Space>
      ),
    },
  ];

  if (requiresCompanySelection) {
    return (
      <Card>
        <Alert
          message="Selection requise"
          description="Selectionnez une entreprise pour acceder aux comptes."
          type="info"
          showIcon
        />
      </Card>
    );
  }

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Card
        title="Comptes"
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchAccounts}
              loading={loadingAccounts}
            >
              Rafraichir
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={openCreateModal}
              disabled={!canCreate || !hasProperties}
            >
              Nouveau compte
            </Button>
          </Space>
        }
      >
        {!hasProperties ? (
          <Alert
            message="Aucune propriete configuree"
            description="Creez une propriete dans l'onglet Configuration pour activer la creation de comptes."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        ) : null}
        <Form layout="vertical">
          <Row gutter={[16, 16]}>
            <Col xs={24} md={6}>
              <Form.Item label="Propriete">
                <Select
                  value={filters.propertyId}
                  onChange={(value) =>
                    setFilters((prev) => ({ ...prev, propertyId: value }))
                  }
                  options={[
                    { value: "all", label: "Tous" },
                    ...properties.map((property) => ({
                      value: property.id,
                      label: property.name,
                    })),
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Type">
                <Select
                  value={filters.accountType}
                  onChange={(value) =>
                    setFilters((prev) => ({ ...prev, accountType: value }))
                  }
                  options={[
                    { value: "all", label: "Tous" },
                    { value: "corporate", label: "Entreprise" },
                    { value: "agency", label: "Agence" },
                    { value: "group", label: "Groupe" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Actif">
                <Select
                  value={filters.isActive}
                  onChange={(value) =>
                    setFilters((prev) => ({ ...prev, isActive: value }))
                  }
                  options={[
                    { value: "all", label: "Tous" },
                    { value: "true", label: "Actif" },
                    { value: "false", label: "Inactif" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={6}>
              <Form.Item label="Recherche">
                <Input
                  placeholder="Nom, contact, email"
                  value={filters.search}
                  onChange={(event) =>
                    setFilters((prev) => ({
                      ...prev,
                      search: event.target.value,
                    }))
                  }
                  allowClear
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
        <Table
          columns={columns}
          dataSource={filteredAccounts}
          rowKey="id"
          loading={loadingAccounts}
          pagination={{ pageSize: 8 }}
        />
      </Card>

      <Modal
        title={editingAccount ? "Modifier compte" : "Nouveau compte"}
        open={isModalOpen}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingAccount(null);
        }}
        onOk={handleSaveAccount}
        okText="Enregistrer"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Propriete"
            name="propertyId"
            rules={[{ required: true, message: "Propriete requise" }]}
          >
            <Select
              disabled={Boolean(editingAccount)}
              options={properties.map((property) => ({
                value: property.id,
                label: property.name,
              }))}
            />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Type"
                name="accountType"
                rules={[{ required: true, message: "Type requis" }]}
              >
                <Select
                  options={[
                    { value: "corporate", label: "Entreprise" },
                    { value: "agency", label: "Agence" },
                    { value: "group", label: "Groupe" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Nom"
                name="name"
                rules={[{ required: true, message: "Nom requis" }]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Contact" name="contactName">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Email" name="email">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Telephone" name="phone">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Numero fiscal" name="taxNumber">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Adresse" name="address">
            <Input />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Ville" name="city">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Pays" name="country">
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item label="Conditions de paiement" name="paymentTerms">
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Limite de credit" name="creditLimit">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Actif" name="isActive" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
};

export default AccountsTab;
